import {AfterViewInit, Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../api-request.service";
import {UtilsService} from "../utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {ApiDataSource} from "../../utils/api-data-source";
import {AssetsService} from "../../assets/assets.service";
import {AssetsFilters} from "../../assets/assets-filter/assets-filter.interface";
import {AssetsFilterComponent} from "../../assets/assets-filter/assets-filter.component";
import {RBACService} from "../rbac.service";
import { ASSET_BASIC_FLAGS_LEGEND } from '../../assets/asset-flags-legend';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';

@Component({
  selector: 'app-assets-selector',
  templateUrl: './assets-selector.component.html',
  styleUrls: ['./assets-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsSelectorComponent implements OnInit, AfterViewInit {

  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'thumbnail',
    'id',
    'name',
    'code',
    'asset_type',
    'flags',
    'status',
    'owner_type',
    'condition_rating',
    'site'
  ];
  dataSource: AssetsSelectorDataSource;
  returnAllSelected = false;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public assetsService: AssetsService,
    private rbac: RBACService,
    public dialogRef: MatDialogRef<AssetsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // set multiple if it is not present to false
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // set required if it is not present to false
    if (typeof this.data['required'] === 'undefined') {
      this.data['required'] = false;
    }

    // set site_id if it is not present to null
    if (typeof this.data['site_id'] === 'undefined') {
      this.data['site_id'] = null;
    }

    // construct the datasource
    this.dataSource = new AssetsSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // Apply default site filter if a valid site_id is passed into the selector.
    const siteId = this.data['site_id'] ? Number(this.data['site_id']) : null;

    if (siteId && Number.isInteger(siteId) && siteId > 0) {
      this.dataSource.filters.site_ids = [siteId];
    }

    // set a default selection of nothing if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // loop and select the values
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }

    this.returnAllSelected = this.data.returnAllSelected;
  }

  ngOnInit() {
    // Check RBAC permission: standalone assets:view (either perspective) OR a parent feature that
    // needs asset selection. Evaluated with exact: false, so any one of these is enough.
    //
    // Beyond the two Asset Register lists, this selector is opened by other features both as a form
    // field (linking assets to an incident, safety observation, audit or TA/JSA/SWMS) and as a list
    // filter. Narrowing this list to the assets permissions alone would break asset selection in
    // every one of those features.
    //
    // MUST stay in sync with AssetsController::index().
    if (!this.rbac.userCanAccess([
      // Standalone permission for the asset register itself.
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_VIEW),
      // Incidents: asset selection on the edit screen, the view tab link dialog and filters.
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_INCIDENTS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_INCIDENTS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_INCIDENTS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_INCIDENTS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_INCIDENTS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_INCIDENTS, RBACService.RBAC_A.A_EDIT),
      // Safety Observations: same three surfaces as incidents.
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS, RBACService.RBAC_A.A_EDIT),
      // Inspections & Audits: asset selection on the audit edit screens. Neither perspective
      // declares a create action, so they contribute edit only.
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_INSPECTIONS_AUDITS, RBACService.RBAC_A.A_EDIT),
      // TA/JSA/SWMS: asset selection in the add and edit dialogs, and the list filters.
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_TA_JSA_SWMS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_TA_JSA_SWMS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_TA_JSA_SWMS, RBACService.RBAC_A.A_EDIT)
    ], false)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view assets.');
      this.dialogRef.close();
      return;
    }

    // Get the data. Deliberately here rather than in the constructor so the permission check above
    // always runs before the request is made.
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      // Sort UTC by its corresponding date column.
      if ( this.sort.active == 'date_created_UTC' ) {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Clears the search bar and performs a search.
   *
   * @returns {void}
   */
  onClearSearch(): void {
    // clear the search field in the datasource
    this.dataSource.search = '';
    // search for records
    this.dataSource.getData(true);
  }

  /**
   * Clear the current selection.
   *
   * @returns {void}
   */
  onClearSelected(): void {
    this.dataSource.selection.clear();
  }

  /**
   * Save the selected data and close the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    if (this.returnAllSelected) {
      return this.dialogRef.close({
        selected: this.dataSource.selection.selected,
        isAllSelected: this.dataSource.isAllSelected()
      });
    }

    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      if (this.dataSource.selection.selected.length) {
        response = this.dataSource.selection.selected[0];
      } else {
        response = 0;
      }
    }

    this.dialogRef.close(response);
  }

  /**
   * Closes the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Show component dialog for filtering Assets.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(AssetsFilterComponent, {
        asset_type_ids: this.dataSource.filters.asset_type_ids,
        site_ids: this.dataSource.filters.site_ids,
        contractor_owner_ids: this.dataSource.filters.contractor_owner_ids,
        vendor_ids: this.dataSource.filters.vendor_ids,
        contractor_ids: this.dataSource.filters.contractor_ids,
        user_ids: this.dataSource.filters.user_ids,
        status: this.dataSource.filters.status,
        owner_type: this.dataSource.filters.owner_type,
        condition_rating: this.dataSource.filters.condition_rating,
        is_visible: this.dataSource.filters.is_visible,
        is_mobile: this.dataSource.filters.is_mobile,
        is_safety_critical: this.dataSource.filters.is_safety_critical,
        selector_mode: this.dataSource.filters.selector_mode
      }, {
        width: '1200px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.filters.asset_type_ids = results.asset_type_ids ?? this.dataSource.filters.asset_type_ids;
        this.dataSource.filters.site_ids = results.site_ids ?? this.dataSource.filters.site_ids;
        this.dataSource.filters.contractor_owner_ids = results.contractor_owner_ids ?? this.dataSource.filters.contractor_owner_ids;
        this.dataSource.filters.vendor_ids = results.vendor_ids ?? this.dataSource.filters.vendor_ids;
        this.dataSource.filters.contractor_ids = results.contractor_ids ?? this.dataSource.filters.contractor_ids;
        this.dataSource.filters.user_ids = results.user_ids ?? this.dataSource.filters.user_ids;
        this.dataSource.filters.status = results.status ?? this.dataSource.filters.status;
        this.dataSource.filters.owner_type = results.owner_type ?? this.dataSource.filters.owner_type;
        this.dataSource.filters.condition_rating = results.condition_rating ?? this.dataSource.filters.condition_rating;
        this.dataSource.filters.is_visible = results.is_visible ?? this.dataSource.filters.is_visible;
        this.dataSource.filters.is_mobile = results.is_mobile ?? this.dataSource.filters.is_mobile;
        this.dataSource.filters.is_safety_critical = results.is_safety_critical ?? this.dataSource.filters.is_safety_critical;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Opens the provided URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void}
   */
  onOpenUrl(url: string): void {
    if ( !url ) {
      return;
    }
    this.utils.openUrl(url);
  }

  /**
   * Open the dialog explaining the Asset Flags.
   *
   * @return {void}
   */
  onOpenFlagsLegend(): void {
    this.utils.showComponentDialog(
      InfoDialogComponent,
      ASSET_BASIC_FLAGS_LEGEND,
      {
        width: '760px'
      }
    );
  }

}

export class AssetsSelectorDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';

  // Default to visible assets to guide users toward Mobile app visible selections.
  // This filter is user-clearable; non-visible assets remain accessible.
  filters: AssetsFilters = {
    asset_type_ids: [],
    site_ids: [],
    contractor_owner_ids: [],
    vendor_ids: [],
    contractor_ids: [],
    user_ids: [],
    status: 'all',
    owner_type: 'all',
    condition_rating: 'all',
    is_visible: 'true',
    is_mobile: 'all',
    is_safety_critical: 'all',
    selector_mode: true
  };

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/assets', resetOffset, {
      asset_type_ids: this.filters.asset_type_ids.join(','),
      site_ids: this.filters.site_ids.join(','),
      contractor_owner_ids: this.filters.contractor_owner_ids.join(','),
      vendor_ids: this.filters.vendor_ids.join(','),
      contractor_ids: this.filters.contractor_ids.join(','),
      user_ids: this.filters.user_ids.join(',') ,
      status: this.filters.status ?? 'all',
      owner_type: this.filters.owner_type ?? 'all',
      condition_rating: this.filters.condition_rating ?? 'all',
      is_visible: this.filters.is_visible ?? 'true',
      is_mobile: this.filters.is_mobile ?? 'all',
      is_safety_critical: this.filters.is_safety_critical ?? 'all'
    });
  }
}
